import React, { useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { compressToDataUrl } from "./imageCompressor";

/**
 * Dual uploads for the card's main (background) image and the logo.
 * Uses the same iterative canvas compressor as before so the payload
 * that lands in `background_image_data_url` / `logo_image_data_url`
 * is byte-for-byte identical to the pre-refactor version.
 */
export default function ImageUploads({ form, setForm }) {
  const [busy, setBusy] = useState("");
  const bgFileRef = useRef();
  const logoFileRef = useRef();

  const pickImage = (kind) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setBusy(kind);
      try {
        const dataUrl = await compressToDataUrl(file, { keepAlpha: kind === "logo" });
        setForm((f) => ({
          ...f,
          [kind === "background" ? "background_image_data_url" : "logo_image_data_url"]: dataUrl,
        }));
        toast.success(`${kind === "background" ? "Background" : "Logo"} uploaded`);
      } catch (err) {
        toast.error(err?.message || "Could not process that image.");
      } finally {
        setBusy("");
      }
    };
    input.click();
  };

  const clearImage = (kind) => {
    setForm((f) => ({
      ...f,
      [kind === "background" ? "background_image_data_url" : "logo_image_data_url"]: "",
    }));
  };

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="gc-uploads">
      <div>
        <Label>Main image</Label>
        <div className="mt-1 border border-dashed border-border rounded-lg h-28 flex items-center justify-center bg-muted/30 overflow-hidden relative">
          {form.background_image_data_url ? (
            <>
              <img src={form.background_image_data_url} alt="Main image" className="w-full h-full object-cover" />
              <button type="button" onClick={() => clearImage("background")} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center" aria-label="Remove image" data-testid="gc-bg-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full mt-2 text-xs"
          onClick={() => pickImage("background")}
          disabled={busy === "background"}
          data-testid="gc-bg-upload"
        >
          {busy === "background" ? "Compressing…" : (form.background_image_data_url ? "Replace image" : "Upload main image")}
        </Button>
        <p className="text-[10px] text-muted-foreground mt-1">Movable — control its X / Y / Size under Overlay Elements below.</p>
        <input ref={bgFileRef} type="file" className="hidden" accept="image/*" />
      </div>
      <div>
        <Label>Logo</Label>
        <div className="mt-1 border border-dashed border-border rounded-lg h-28 flex items-center justify-center bg-muted/30 overflow-hidden relative">
          {form.logo_image_data_url ? (
            <>
              <img src={form.logo_image_data_url} alt="Logo" className="max-w-full max-h-full object-contain" />
              <button type="button" onClick={() => clearImage("logo")} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center" aria-label="Remove logo" data-testid="gc-logo-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <Sparkles className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full mt-2 text-xs"
          onClick={() => pickImage("logo")}
          disabled={busy === "logo"}
          data-testid="gc-logo-upload"
        >
          {busy === "logo" ? "Compressing…" : (form.logo_image_data_url ? "Replace logo" : "Upload logo")}
        </Button>
        <input ref={logoFileRef} type="file" className="hidden" accept="image/*" />
      </div>
    </div>
  );
}
